import { getJWTToken } from '../../utils/auth';
import type { FocusByDomainContent } from './domainFocus';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type PlanGenerationInputs = {
  focusByDomain?: Record<string, unknown>;
  accommodations?: unknown;
  planSnapshot?: unknown;
};

export type PlanSnapshotInput = {
  studentId: string;
  fullName: string;
  school: string | null;
  grade: string | null;
  elTeacher: string | null;
  demographics: unknown;
  focusByDomain: Record<string, FocusByDomainContent>;
  accommodations: string[] | null;
  suggestedItems?: string[] | null;
  planSnapshot?: unknown;
  inputs?: PlanGenerationInputs;
  student?: unknown;
  criteria?: unknown;
};

function byDomain<T>(source: Record<string, T> | undefined): Record<string, T> {
  const ordered: Record<string, T> = {};
  for (const domain of Object.keys(source ?? {}).sort()) {
    const value = source?.[domain];
    if (value !== undefined) ordered[domain] = value;
  }
  return ordered;
}

export function buildPlanSnapshot(input: PlanSnapshotInput): Record<string, unknown> {
  return {
    studentId: input.studentId,
    fullName: input.fullName,
    school: input.school,
    grade: input.grade,
    elTeacher: input.elTeacher,
    demographics: input.demographics ?? null,
    focusByDomain: byDomain(input.focusByDomain),
    accommodations: input.accommodations ?? null,
    suggestedItems: input.suggestedItems ?? null,
    planSnapshot: input.planSnapshot ?? null,
    student: input.student ?? null,
    criteria: input.criteria ?? null,
    inputs: {
      focusByDomain: byDomain(input.inputs?.focusByDomain),
      accommodations: input.inputs?.accommodations ?? null,
      planSnapshot: input.inputs?.planSnapshot ?? null,
    },
  };
}

export type PersistedStudentPlan = {
  studentId: string;
  version: number;
  generatedAt: string;
  schoolYear?: string;
  dataVersion?: number;
  plan: unknown;
};

export async function saveStudentPlan(studentId: string, plan: unknown): Promise<{ version: number }> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/students/${encodeURIComponent(studentId)}/plan`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(plan),
  });
  if (!response.ok) {
    throw new Error(`save-student-plan failed: ${response.status}`);
  }
  const body = (await response.json()) as { version: number };
  return { version: body.version };
}

export async function fetchStudentPlan(studentId: string): Promise<PersistedStudentPlan | null> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/students/${encodeURIComponent(studentId)}/plan`, {
    headers: authHeaders(token),
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`fetch-student-plan failed: ${response.status}`);
  }
  return (await response.json()) as PersistedStudentPlan;
}
